import { LocalDate } from '@js-joda/core';
import { useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';
import styled from 'styled-components';

import { Country } from '@dreryk/address-common-api';
import { GuardianExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import {
   NationalIdentificationNumber,
   NationalIdentificationNumberCountry,
   Passport,
   Pesel,
} from '@dreryk/personal-external-identifier-common-api';

import { assertUnreachable, FormContextValues, useFormController } from '@dreryk/react-common';
import { Button, ButtonVariant, FormController, Modal, ModalWidth } from '@dreryk/react-components';

import { guardianTranslationKeys } from '../../../../../../translations';
import { MedicalLeaveFormAddress } from '../../../../common/common.types';
import { mapPatientAddressToMedicalLeaveFormAddress } from '../../../../common/common.utils';
import { FirstNameField } from '../../../../common/formSections/personalData/FirstNameField';
import { LastNameField } from '../../../../common/formSections/personalData/LastNameField';
import { MiddleNameField } from '../../../../common/formSections/personalData/MiddleNameField';
import {
   createGuardianExternalIdentifierTypeVisitor,
   GuardianExternalIdentifierType,
   GuardianExternalIdentifierTypeField,
   GuardianExternalIdentifierTypeVisitor,
} from '../../../../common/formSections/personalData/guardianExternalIdentifier/GuardianExternalIdentifierTypeField';
import {
   GuardianExternalIdentifierValue,
   GuardianExternalIdentifierValueField,
} from '../../../../common/formSections/personalData/guardianExternalIdentifier/GuardianExternalIdentifierValueField';
import { PassportCountryField } from '../../../../common/formSections/personalData/guardianExternalIdentifier/PassportCountryField';
import { PassportValidToField } from '../../../../common/formSections/personalData/guardianExternalIdentifier/PassportValidToField';

import { PatientSearch } from './PatientSearch/PatientSearch';
import { PatientSearchPatient } from './PatientSearch/PatientSearch.types';
import { StyledGuardianModalOpenButton } from './StyledGuardianModalOpenButton';

export const StyledGuardianModalContent = styled.div`
   display: flex;
   flex-direction: column;
   gap: 24px;
`;
StyledGuardianModalContent.displayName = 'StyledGuardianModalContent';

export const StyledGuardianFormContent = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr 1fr;
   gap: 24px;
`;
StyledGuardianFormContent.displayName = 'StyledGuardianFormContent';

type GuardianModalFormBean = {
   firstName: string | null;
   middleName: string | null;
   lastName: string | null;
   guardianExternalIdentifierType: GuardianExternalIdentifierType;
   guardianExternalIdentifierValue: GuardianExternalIdentifierValue;
   passportCountry: Country | null;
   passportValidTo: LocalDate | null;
   nationalIdentificationNumberCountry: NationalIdentificationNumberCountry | null;
   address: MedicalLeaveFormAddress | null;
   birthDate: LocalDate | null;
};

const INITIAL_VALUES: GuardianModalFormBean = {
   firstName: null,
   middleName: null,
   lastName: null,
   address: null,
   guardianExternalIdentifierType: 'Pesel',
   guardianExternalIdentifierValue: null,
   nationalIdentificationNumberCountry: null,
   passportCountry: null,
   passportValidTo: null,
   birthDate: null,
};

export type GuardianModalFormController = FormContextValues<GuardianModalFormBean>;
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export type GuardianModalFormSubmitHandler = Parameters<GuardianModalFormController['handleSubmit']>[0];

export const createGuardianExternalIdentifier = (guardian: GuardianModalFormBean): GuardianExternalIdentifier => {
   const guardianExternalIdentifierTypeVisitor = createGuardianExternalIdentifierTypeVisitor(
      guardian.guardianExternalIdentifierType
   );

   return GuardianExternalIdentifier.of(
      guardianExternalIdentifierTypeVisitor<Pesel | Passport | NationalIdentificationNumber>({
         visitPesel: () => Pesel.of(guardian.guardianExternalIdentifierValue!),
         visitPassport: () =>
            Passport.of(
               guardian.passportCountry!,
               guardian.guardianExternalIdentifierValue!,
               guardian.passportValidTo!
            ),
      })
   );
};

type GuardianModalProps = {
   readonly onSubmit: GuardianModalFormSubmitHandler;
   readonly patientId?: number;
   readonly checkGuardianAlreadyAdded: (
      type: GuardianExternalIdentifierType,
      value: GuardianExternalIdentifierValue
   ) => boolean;
   readonly checkGuardianSameAsPatient: (
      type: GuardianExternalIdentifierType,
      value: GuardianExternalIdentifierValue
   ) => boolean;
};

// eslint-disable-next-line max-lines-per-function
export const GuardianModal = (props: GuardianModalProps): JSX.Element => {
   const { register, setValue, handleSubmit, reset, setFocus, watch } = useFormController<GuardianModalFormBean>(
      INITIAL_VALUES
   );

   const [guardianDataModalVisible, toggleGuardianDataModalVisible] = useToggle(false);

   const { guardianExternalIdentifierType } = watch([
      'guardianExternalIdentifierType',
      'guardianExternalIdentifierValue',
   ]);

   const guardianExternalIdentifierTypeVisitor = useMemo<GuardianExternalIdentifierTypeVisitor>(
      () => createGuardianExternalIdentifierTypeVisitor(guardianExternalIdentifierType),
      [guardianExternalIdentifierType]
   );

   const handleGuardianExternalIdentifierType = useCallback(() => {
      setValue('guardianExternalIdentifierValue', null);
      setFocus('guardianExternalIdentifierValue');
   }, [setValue, setFocus]);

   const handleFormSubmit = handleSubmit((values: GuardianModalFormBean): void => {
      props.onSubmit(values);
      reset(INITIAL_VALUES);
      toggleGuardianDataModalVisible();
   });

   const handlePatientSelected = useCallback(
      (patient: PatientSearchPatient | null) => {
         if (!patient) {
            return reset(INITIAL_VALUES);
         }

         const personalData = {
            firstName: patient.firstName?.asString() ?? null,
            middleName: patient.middleName?.asString() ?? null,
            lastName: patient.lastName?.asString() ?? null,
            address: patient.address ? mapPatientAddressToMedicalLeaveFormAddress(patient.address) : null,
            birthDate: patient.birthDate ?? null,
            passportCountry: null,
            passportValidTo: null,
            nationalIdentificationNumberCountry: null,
         } as const;

         return reset(
            patient.externalIdentifier!.accept<GuardianModalFormBean>({
               visitPesel: (pesel) => ({
                  ...personalData,
                  guardianExternalIdentifierType: 'Pesel',
                  guardianExternalIdentifierValue: pesel.asString(),
               }),
               visitGuardianPesel: () => assertUnreachable(undefined),
               visitPassport: (passport) => ({
                  ...personalData,
                  guardianExternalIdentifierType: 'Passport',
                  guardianExternalIdentifierValue: passport.getValue(),
                  passportCountry: passport.getCountry(),
                  passportValidTo: passport.getValidTo(),
               }),
               visitNationalIdentificationNumber: () => assertUnreachable(undefined),
            })
         );
      },
      [reset]
   );

   return (
      <StyledGuardianModalOpenButton>
         <Button
            variant={ButtonVariant.SECONDARY}
            label={guardianTranslationKeys.addNewGuardian}
            onClick={toggleGuardianDataModalVisible}
            type={'button'}
            testId={'enterGuardianData'}
         />
         {guardianDataModalVisible ? (
            <Modal
               title={guardianTranslationKeys.addGuardian}
               onCancel={toggleGuardianDataModalVisible}
               onSubmit={handleFormSubmit}
               width={ModalWidth.HUGE}
               confirmationButtonLabel={guardianTranslationKeys.save}
               testId={'guardianData'}
               wrapInForm
            >
               <StyledGuardianModalContent>
                  <PatientSearch onPatientSelected={handlePatientSelected} visitPatientId={props.patientId} />
                  <StyledGuardianFormContent>
                     <FirstNameField name={'firstName'} register={register} required />
                     <MiddleNameField name={'middleName'} register={register} />
                     <LastNameField name={'lastName'} register={register} required />

                     <GuardianExternalIdentifierTypeField
                        name={'guardianExternalIdentifierType'}
                        register={register}
                        onSelect={handleGuardianExternalIdentifierType}
                     />
                     <GuardianExternalIdentifierValueField
                        name={'guardianExternalIdentifierValue'}
                        register={register}
                        guardianExternalIdentifierTypeVisitor={guardianExternalIdentifierTypeVisitor}
                        checkGuardianAlreadyAdded={props.checkGuardianAlreadyAdded}
                        checkGuardianSameAsPatient={props.checkGuardianSameAsPatient}
                     />
                     {guardianExternalIdentifierTypeVisitor({
                        visitPesel: () => null,

                        visitPassport: () => (
                           <>
                              <div />
                              <PassportCountryField name={'passportCountry'} register={register} />
                              <PassportValidToField name={'passportValidTo'} register={register} />
                           </>
                        ),
                     })}

                     <FormController controller={register('address')} />
                     <FormController controller={register('birthDate')} />
                  </StyledGuardianFormContent>
               </StyledGuardianModalContent>
            </Modal>
         ) : null}
      </StyledGuardianModalOpenButton>
   );
};
